/**
 * Imperative confirm dialog API.
 *
 * Module-level store (same pattern as use-toast.ts). The companion
 * `ConfirmDialogProvider` component subscribes to this store and renders
 * the actual AlertDialog at the app root.
 *
 * Usage:
 *   import { confirm } from "@/components/confirmDialog";
 *
 *   const ok = await confirm({
 *     title: "Delete session",
 *     description: "This permanently removes the session.",
 *     confirmLabel: "Delete",
 *     destructive: true,
 *   });
 *   if (ok) { deleteMutation.mutate(...) }
 */

import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Public API types
// ---------------------------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Optional extra body content rendered between description and footer. */
  body?: ReactNode;
  /**
   * When true, the confirm button shows a spinner and the dialog stays open
   * until the caller calls `close()`. Useful for mutations that should keep
   * the dialog visible while pending.
   */
  pending?: boolean;
}

export interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

// ---------------------------------------------------------------------------
// Module-level store
// ---------------------------------------------------------------------------

type Listener = (state: ConfirmState | null) => void;

export const listeners: Listener[] = [];
export let current: ConfirmState | null = null;

function notify() {
  for (const listener of listeners) {
    listener(current);
  }
}

/**
 * Show a confirm dialog imperatively. Returns a promise that resolves
 * to `true` (confirmed) or `false` (cancelled / dismissed).
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  // If a dialog is already open, reject the previous one.
  if (current) {
    current.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    current = { ...options, resolve };
    notify();
  });
}

/**
 * Update the currently-open confirm dialog (e.g. to toggle `pending`).
 * No-ops if no dialog is open.
 */
export function updateConfirm(patch: Partial<ConfirmOptions>): void {
  if (!current) return;
  current = { ...current, ...patch };
  notify();
}

/**
 * Programmatically close the confirm dialog, resolving with `false`.
 */
export function closeConfirm(): void {
  if (!current) return;
  current.resolve(false);
  current = null;
  notify();
}

/**
 * Internal helper for the provider to clear state after resolving.
 */
export function _clearCurrent(): void {
  current = null;
  notify();
}
